import UAParser from 'ua-parser-js';
import { v4 } from 'uuid';

import { getUrlParams, parseJwt } from '@/helpers';
import { IUserToken } from '@/interfaces/accounts';
import IApiResponse from '@/interfaces/response';
import { setUser } from '@/redux/auth/slice';
import { store } from '@/redux/store';
import { ACCOUNTS_AMBIENT, API_V4_BASE_URL, BUILD_NUMBER, tokenKeysName } from '@/settings';

import { post } from './api';

let loginPromise: Promise<any> | null = null;

const login = (accountsToken: string, courseHash = '') => {
  if (loginPromise) return loginPromise;

  loginPromise = new Promise(async (resolve, reject) => {
    try {
      const response = await post<IApiResponse>({
        url: '/oauth/eduzzaccount/validate',
        withCredentials: true,
        baseURL: API_V4_BASE_URL,
        data: { token: accountsToken, ...getDeviceInfo(), course_hash: courseHash }
      });
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { token, refresh_token } = response.data.data;

      updateToken(token, refresh_token);
      resolve(parseJwt(token));
    } catch (err) {
      logout();
      reject(err);
    } finally {
      loginPromise = null;
    }
  });

  return loginPromise;
};

export const makeLogin = async (accountsToken: string, courseHash = '') => {
  return login(accountsToken, courseHash);
};

export const isPossibleRefresh = async () => {
  try {
    const refreshToken = localStorage.getItem(tokenKeysName.refreshToken);
    const currentToken = localStorage.getItem(tokenKeysName.token);

    if (!refreshToken || !currentToken) return false;

    if (!isExpiredToken()) return true;

    const response = await post({
      url: '/oauth/refresh',
      withCredentials: true,
      baseURL: API_V4_BASE_URL,
      headers: {
        RefreshToken: refreshToken,
        Authorization: `Bearer ${currentToken}`,
        FrontVersion: BUILD_NUMBER || 'local'
      },
      params: {
        startWhen: 'isPossibleRefresh'
      }
    });

    const token = response.data?.data?.token.replace('Bearer ', '') || 'invalid';

    if (token === 'invalid') return false;

    updateToken(token);

    return true;
  } catch (err) {
    logout();

    return false;
  }
};

const clearTokens = () => {
  localStorage.removeItem(tokenKeysName.token);
  localStorage.removeItem(tokenKeysName.expiration);
  localStorage.removeItem(tokenKeysName.refreshToken);
};

export const checkIsAuthenticated = () => {
  const userInfo = parseJwt<IUserToken>(localStorage.getItem(tokenKeysName.token));

  store.dispatch(setUser(userInfo ?? null));
  return isExpiredToken();
};

export const isExpiredToken = () => {
  const exp = getExpiration();
  const currentTime = new Date().getTime() / 1000;

  return exp && exp < currentTime;
};

export const getUserInfo = () => {
  const token = localStorage.getItem(tokenKeysName.token);

  if (token && getExpiration()) {
    return parseJwt(token);
  }

  return null;
};

const getExpiration = () => {
  const exp = Number(localStorage.getItem(tokenKeysName.expiration));

  if (exp > 0) {
    return exp / 1000;
  }

  return 0;
};

export const updateToken = (token: string, refreshToken = '') => {
  if (!token) {
    return;
  }

  if (token === 'invalid') {
    clearTokens();
    logout();
  }

  const claims = parseJwt(token);
  const tokenTtl = claims.exp - claims.iat;
  const tokenExpiration = new Date();

  //Forcing the token to expire 120 seconds earlier
  tokenExpiration.setSeconds(tokenExpiration.getSeconds() + tokenTtl - 120);
  localStorage.setItem(tokenKeysName.token, token);
  localStorage.setItem(tokenKeysName.expiration, tokenExpiration.getTime().toString());

  if (refreshToken) {
    localStorage.setItem(tokenKeysName.refreshToken, refreshToken);
  }

  store.dispatch(setUser(parseJwt(token)));
};

export const logout = (redirect = '') => {
  clearTokens();

  if (redirect.length <= 0) {
    redirect = window.location.pathname + window.location.search;
  }

  (window as any).Eduzz.Accounts.logout({
    redirectTo: window.location.origin + redirect,
    env: ACCOUNTS_AMBIENT
  });
};

export const getDeviceInfo = (): { device_id: string; device_name: string } => {
  const params = getUrlParams();
  let deviceId = params.device_id || localStorage.getItem('deviceId');
  let deviceName = params.device_name || localStorage.getItem('deviceName');

  if (!deviceId) {
    deviceId = setDeviceId();
  }

  if (!deviceName) {
    deviceName = setDeviceName();
  }

  return {
    device_id: deviceId,
    device_name: deviceName
  };
};

const setDeviceId = () => {
  const deviceId = v4();

  localStorage.setItem('deviceId', deviceId.toString());

  return deviceId;
};

const setDeviceName = () => {
  const parser = new UAParser();
  const {
    browser: { name: browser },
    os: { name: device }
  } = parser.getResult();
  const deviceName = `${browser} - ${device}`;

  localStorage.setItem('deviceName', deviceName);

  return deviceName;
};

export default { checkIsAuthenticated, isPossibleRefresh, logout, updateToken };
